import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FrameworkPayload,
  createFramework,
  getFramework,
  updateFramework,
} from "../services/frameworkAdminService";
import { usePermissionLevel } from "../hooks/usePermissionLevel";

interface FrameworkEditorPageProps {
  getToken: () => string | undefined;
}

const FrameworkEditorPage: React.FC<FrameworkEditorPageProps> = ({
  getToken,
}) => {
  const navigate = useNavigate();
  const { orgSlug, frameworkId } = useParams();
  const { isAdmin } = usePermissionLevel();
  const isEdit = Boolean(frameworkId);

  const listPath = `/${orgSlug}/rubrics?tab=frameworks`;

  const [form, setForm] = useState<FrameworkPayload>({
    name: "",
    description: "",
    instructions: "",
  });
  const [isGlobal, setIsGlobal] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const loadAll = useCallback(async () => {
    if (!isEdit || !frameworkId) return;
    try {
      const f = await getFramework(frameworkId, getToken());
      setForm({
        name: f.name,
        description: f.description,
        instructions: f.instructions,
      });
      setIsGlobal(f.isGlobal);
      setCanEdit(f.canEdit);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [getToken, isEdit, frameworkId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      if (isEdit && frameworkId) {
        // Only an admin may set this — sending it as a manager earns a 403.
        // isAdmin already gates the checkbox, but re-check here so a stale value
        // can never sneak into a manager's save payload.
        await updateFramework(
          frameworkId,
          isAdmin ? { ...form, isGlobal } : form,
          token,
        );
      } else {
        // createFramework hardcodes isGlobal false server-side; promotion is a
        // separate, admin-only act on an existing row.
        await createFramework(form, token);
      }
      navigate(listPath);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      setSaving(false);
    }
  };

  const set = <K extends keyof FrameworkPayload>(
    k: K,
    v: FrameworkPayload[K],
  ) => setForm((f) => ({ ...f, [k]: v }));

  const incomplete =
    !form.name.trim() || !form.description.trim() || !form.instructions.trim();

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-2xl w-full mx-auto px-8 py-10 text-on-surface">
        <button
          type="button"
          className="text-slate-400 text-sm mb-4"
          onClick={() => navigate(listPath)}
        >
          ‹ Back to Frameworks
        </button>
        <h1 className="text-xl font-bold mb-4">
          {isEdit ? "Edit Framework" : "New Framework"}
        </h1>
        {error && <div className="mb-3 text-error text-sm">{error}</div>}

        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : (
          <>
            {isEdit && !canEdit && (
              <p className="text-slate-400 text-sm mb-4">
                This framework belongs to the shared catalog and is read-only here.
              </p>
            )}

            <div className="mb-4">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Description</label>
              <textarea
                className="form-control h-24"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Grading instructions</label>
              <textarea
                className="form-control h-64"
                value={form.instructions}
                onChange={(e) => set("instructions", e.target.value)}
              />
            </div>

            {isEdit && isAdmin && (
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  disabled={saving}
                  className="h-4 w-4 rounded border-outline-variant/40 bg-surface-container-low"
                />
                <span className="text-xs font-label uppercase tracking-wider text-slate-400">
                  Shared catalog — visible to every organization
                </span>
              </label>
            )}

            <div className="flex gap-3 mt-4">
              <button
                type="button"
                className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                disabled={saving || incomplete || (isEdit && !canEdit)}
                onClick={save}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm text-slate-300"
                onClick={() => navigate(listPath)}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FrameworkEditorPage;
